/**
 * Tickets Hooks
 *
 * React Query hooks for the Tickets page: the org's default ticket source, the
 * tagged-ticket list and the Jira tag-trigger settings that configure it. Runs are
 * started by the Jira webhook, never from the UI.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type {
  TicketEnvironmentOption,
  TicketSettingsResponse,
  TicketSettingsUpdate,
  TicketsResponse,
} from './types';

/** `defaultProviderId` is null when several providers are connected and none is chosen. */
export type DefaultTicketSource = { connected: string[]; defaultProviderId: string | null };

const DEFAULT_TICKET_SOURCE_KEY = ['default-ticket-source'];

export function useDefaultTicketSource() {
  return useQuery({
    queryKey: DEFAULT_TICKET_SOURCE_KEY,
    queryFn: async (): Promise<{ ok: true; data: DefaultTicketSource }> => {
      const response = await fetch(`${API_URL}/integrations/ticketing/default-source`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to fetch the default ticket source');
      return response.json();
    },
  });
}

export function useSetDefaultTicketSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (providerId: string) => {
      const response = await fetch(`${API_URL}/integrations/ticketing/default-source`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ providerId }),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to save the default ticket source');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEFAULT_TICKET_SOURCE_KEY });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

export function useTickets() {
  return useQuery({
    queryKey: ['tickets'],
    queryFn: async (): Promise<TicketsResponse> => {
      const response = await fetch(`${API_URL}/tickets`, { credentials: 'include' });
      if (!response.ok) throw await toApiError(response, 'Failed to fetch tickets');
      return response.json();
    },
  });
}

/** Shares the `jira-settings` key with the Jira settings section so the two stay in sync. */
export function useTicketSettings() {
  return useQuery({
    queryKey: ['jira-settings'],
    queryFn: async (): Promise<TicketSettingsResponse> => {
      const response = await fetch(`${API_URL}/integrations/jira/settings`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to fetch ticket settings');
      return response.json();
    },
    retry: false,
  });
}

export function useUpdateTicketSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (update: TicketSettingsUpdate) => {
      const response = await fetch(`${API_URL}/integrations/jira/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(update),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to save ticket settings');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jira-settings'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
  });
}

/** Environments of one application, for the target picker. */
export function useTicketEnvironments(propertyId: string | null) {
  return useQuery({
    queryKey: ['property-environments', propertyId],
    enabled: Boolean(propertyId),
    queryFn: async (): Promise<{ ok: true; items: TicketEnvironmentOption[] }> => {
      const response = await fetch(`${API_URL}/org/properties/${propertyId}/environments`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to fetch environments');
      return response.json();
    },
  });
}

/** Applications for the target picker. Same key and shape as the Jira settings section. */
export function useTicketProperties() {
  return useQuery({
    queryKey: ['properties'],
    queryFn: async (): Promise<{ ok: boolean; items?: Array<{ id: string; name: string }> }> => {
      const response = await fetch(`${API_URL}/org/properties?pageSize=100`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to fetch applications');
      return response.json();
    },
  });
}
